<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="flex space-x-2 element-island">
          <h1 class="font-semibold text-3xl">
            {{ $t('Reports.MonthlyFinancials.title') }}
          </h1>
        </div>

        <div
          v-if="canExport && reportsMediaCollection.mediaRequestAction.firstLoad.value && reportsMediaCollection.mediaCollection.value.length > 0"
          class="element-island flex items-center space-x-4"
        >
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="showExportReportModal = true"
          >
            {{ $t('Reports.MonthlyFinancials.exportReport') }}
          </button>
        </div>
      </div>

      <div
        v-if="reportsMediaCollection.mediaRequestAction.firstLoad.value && reportsMediaCollection.mediaCollection.value.length > 0"
        class="element-island overflow-y-auto flex flex-1 p-4"
      >
        <div
          class="flex w-full"
        >
          <template v-if="reportsMediaCollection.mediaCollection.value.length > 0">
            <div class="w-1/4 flex">
              <div class="w-full overflow-y-auto relative h-full mr-2">
                <div class="absolute inset-0 overflow-y-auto">
                  <div class="p-2">
                    <ul class="space-y-2">
                      <li
                        v-for="(media, i) in orderedReports"
                        :key="media.uuid"
                        class="p-2 rounded-lg cursor-pointer"
                        :class="{ 'bg-gray-300': media.uuid === selectedReport?.uuid }"
                        @click="selectReport(i)"
                      >
                        <div>
                          {{ $t('Reports.MonthlyFinancials.reportForPeriod') }}
                          {{ transformPeriod(media.custom_properties.period) }}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div class="w-3/4">
              <iframe
                v-if="selectedReport"
                :src="selectedReport.custom_properties.access_url"
                frameborder="0"
                class="w-full h-full"
              />
            </div>
          </template>
        </div>
      </div>

      <div
        v-else-if="reportsMediaCollection.mediaRequestAction.is(States.COMPLETE) && canExport"
        class="element-island m-auto"
      >
        <div class="w-full text-center p-2 px-10">
          <h1 class="text-2xl">
            {{ $t('Reports.MonthlyFinancials.exportMonthlyReport') }}
          </h1>
        </div>

        <div class="flex flex-col space-y-4">
          <label>
            {{ $t('Reports.MonthlyFinancials.month') }}

            <AppInput
              v-model="reportMonth"
              name="month"
              type="month"
              class="form-input"
            />
          </label>

          <button
            type="button"
            class="btn btn--primary-blue"
            @click="exportMonthlyReport"
          >
            {{ $t('Reports.MonthlyFinancials.exportReport') }}
          </button>
        </div>
      </div>

      <div
        v-else-if="reportsMediaCollection.mediaRequestAction.is(States.COMPLETE)"
        class="element-island m-auto"
      >
        <div class="w-full text-center p-2 px-10">
          <h1 class="text-2xl">
            {{ $t('Reports.MonthlyFinancials.noReportsAvailable') }}
          </h1>
        </div>
      </div>

      <div
        v-else
        class="element-island overflow-y-auto flex flex-1 p-4"
      >
        <div
          class="flex justify-center items-center w-full"
        >
          <AppSpinner />
        </div>
      </div>
    </div>

    <GenerateExportModal
      v-if="showExportReportModal && canExport"
      @close="showExportReportModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import GenerateExportModal from '@/components/Reports/MonthlyFinancials/GenerateExportModal.vue';
import { Media, useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForMonthInput } from '@/helpers';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const emits = defineEmits<{
  (e: 'close');
}>();

const reportMonth = ref(dateForMonthInput(new Date()));
const projectsStore = useProjectsStore();
const userStore = useUserStore();
const canExport = computed(() => !userStore.isClientManager());

const project = computed(() => {
  return projectsStore.project;
});

const reportsMediaCollection = useMediaCollection(projectsStore.monthlyReportsIndex);
const selectedReport = ref<Media | undefined>();

const orderedReports = computed(() => {
  return reportsMediaCollection.mediaCollection.value.toSorted((a, b) => {
    return b.custom_properties.period.slice(0, 10).localeCompare(a.custom_properties.period.slice(0, 10));
  });
});

const selectReport = (index: number) => {
  selectedReport.value = orderedReports.value[index];
};

watch(orderedReports, (newValue) => {
  if(newValue) {
    newValue;
    selectedReport.value = newValue[0];
  }
}, { immediate: true });

watch(() => project.value?.id, (newValue) => {
  if(newValue) {
    reportsMediaCollection.clear();

    reportsMediaCollection.fetchMedia(newValue);
  }
}, { immediate: true });

const exportMonthlyReportAction = useStoreApiAction(projectsStore.monthlyReport);

const exportMonthlyReport = () => {
  exportMonthlyReportAction.request(project.value.id, { month: reportMonth.value }).then(() => {
    Swal.fire({
      icon: 'success',
      title: t('Reports.MonthlyFinancials.monthlyReportGenerating'),
      text: t('Reports.MonthlyFinancials.emailNotification'),
    }).then(() => {
      emits('close');
    });
  }).catch((error) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: t('Reports.MonthlyFinancials.pdfCurrentlyExporting'),
        text: error.data.message,
      });
    } else if(error.status === 422) {
      Swal.fire({
        icon: 'error',
        title: t('Reports.MonthlyFinancials.failedToExport'),
        text: error.data.message,
      });
    }
  });
};

const transformPeriod = (date: string) => {
  const start = date.slice(0, 10);
  const end = date.slice(11, 22);

  return `${DateTime.fromFormat(start, 'yyyy-MM-dd').toLocaleString()} to ${
    DateTime.fromFormat(end, 'yyyy-MM-dd').toLocaleString()
  }`;
};

const showExportReportModal = ref(false);
</script>

<style scoped></style>
